import { BlueprintInstanceType } from '../shared.enums';
import { breakpointData } from './breakpoint.data';
import { Publish } from '../../decorators';
import { ReferenceMap } from '../public.types';
import { snapToAdjacentGroupEdgeCfn, snapToNearestGroupCenterCfn } from './functions';

import { BreakpointDataIn, BreakpointDataOut } from './types';
import { KitchenDesignBase, KitchenDesignOptions } from '../kitchen-design-base';

export class Breakpoint extends KitchenDesignBase<BreakpointDataIn> {
	#center: number;
	#ruleManaged: boolean;

	constructor (options: KitchenDesignOptions<BreakpointDataIn> = {}) {
		super(BlueprintInstanceType.Breakpoint, options);
		const data = breakpointData(options.dataIn ?? null);

		this._id = data.id;
		this.#center = data.center;
		this.#ruleManaged = data.ruleManaged;

		this.setInitialized()

		// cleans up dimensions when a breakpoint is removed
		// ?? This type of logic may be better served in the class that owns dimensions. Let it clean itself up using subscriptions
		this.subscribe('*', 'deleted', (event) => {
			const dimensions = (event.payload.ref as ReferenceMap)?.kitchen.design?.dimensions ?? null;
			if (dimensions) dimensions.delete(this.id);
		});
	}

	@Publish('breakpoint_center', 'updated')
	set center (center: number) { this.#center = center; }
	get center (): number {	return this.#center; }

	/**
	 * True when the rules engine placed this seam, false when a designer placed it by hand.
	 * Only rule-managed breakpoints are regenerated or removed by the seam rule. Deliberately
	 * not published -- it is provenance metadata, and publishing it inside a rule run would
	 * re-enter the rules engine.
	 */
	set ruleManaged (ruleManaged: boolean) { this.#ruleManaged = ruleManaged; }
	get ruleManaged (): boolean { return this.#ruleManaged; }

	/**
	 * Snaps the breakpoint to the right edge of the appliance column to its left
	 */
	public snapLeft (): void { snapToAdjacentGroupEdgeCfn(this as Breakpoint, 'left'); }

	/**
	 * Snaps the breakpoint to the left edge of the appliance column to its right
	 */
	public snapRight (): void { snapToAdjacentGroupEdgeCfn(this as Breakpoint, 'right'); }

	/**
	 * Snaps the breakpoint to the centre of the nearest appliance column
	 */
	public snapCenter (): void { snapToNearestGroupCenterCfn(this as Breakpoint); }

	public serialize (): BreakpointDataOut {
		const record: BreakpointDataOut = {
			id: this._id,
			center: this.center
		};

		if (this.#ruleManaged === true) record.ruleManaged = true;

		return record;
	}
}
